/**
 * As colunas que ladeiam o domo: agenda e tarefas à esquerda, conectores à
 * direita.
 *
 * ## Uma decisão que atravessa as três
 *
 * Nenhuma delas inventa dado. Coluna vazia diz **por que** está vazia — "nada
 * marcado" é diferente de "não consegui ler a agenda", e uma tela que mostra os
 * dois como o mesmo espaço em branco faz a pessoa desconfiar do produto inteiro
 * quando o problema era a rede.
 */

import { useMemo } from 'react';
import { Passos, proximoExecutavel } from './Passos';
import { TopoDoCartao } from './Selo';
import type { ConectorNaTela, EventoDaAgenda, PassoNaTela, TarefaNaTela } from './usePainel';

// ---------------------------------------------------------------------------
// agenda
// ---------------------------------------------------------------------------

const DIA_LONGO = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

const chaveDoDia = (iso: string): string => iso.slice(0, 10);

/** Hoje e amanhã ganham nome; o resto vira data. É como se fala de agenda. */
function rotuloDoDia(iso: string, hojeISO: string): string {
  const dia = chaveDoDia(iso);
  if (dia === hojeISO) return 'Hoje';
  const amanha = new Date(`${hojeISO}T12:00:00`);
  amanha.setDate(amanha.getDate() + 1);
  if (dia === amanha.toISOString().slice(0, 10)) return 'Amanhã';
  // Só a PRIMEIRA letra sobe. `text-transform: capitalize` no CSS subiria
  // todas — "qua., 12 de ago." virava "Qua., 12 De Ago.", que não é português.
  const formatado = DIA_LONGO.format(new Date(iso));
  return formatado.charAt(0).toUpperCase() + formatado.slice(1);
}

export function Agenda({
  eventos,
  falhou,
}: {
  eventos: readonly EventoDaAgenda[];
  falhou: boolean;
}): JSX.Element {
  const hojeISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const porDia = useMemo(() => {
    const mapa = new Map<string, EventoDaAgenda[]>();
    for (const e of eventos) {
      const dia = chaveDoDia(e.inicio);
      const lista = mapa.get(dia) ?? [];
      lista.push(e);
      mapa.set(dia, lista);
    }
    return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [eventos]);

  return (
    <section className="u1-card" aria-labelledby="u1-h-agenda">
      <TopoDoCartao
        titulo="Agenda"
        id="u1-h-agenda"
        estado={falhou ? 'parcial' : eventos.length === 0 ? 'frio' : 'vivo'}
      />

      {falhou ? (
        <p className="u1-card__vazio u1-card__vazio--falha">Não consegui ler a agenda agora.</p>
      ) : porDia.length === 0 ? (
        <p className="u1-card__vazio">Nada marcado.</p>
      ) : (
        <ol className="u1-agenda">
          {porDia.map(([dia, doDia]) => (
            <li key={dia} className="u1-agenda__dia">
              <p className="u1-agenda__data">{rotuloDoDia(`${dia}T12:00:00`, hojeISO)}</p>
              <ul className="u1-agenda__itens">
                {doDia.map((e) => (
                  <li key={e.id} className="u1-agenda__evento">
                    <span className="u1-agenda__hora">{HORA.format(new Date(e.inicio))}</span>
                    <span className="u1-agenda__titulo">{e.titulo}</span>
                    {e.local !== null && e.local !== '' && (
                      <span className="u1-agenda__local">{e.local}</span>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// tarefas
// ---------------------------------------------------------------------------

/**
 * O estado da tarefa como a pessoa o chama, e a cor que ele merece.
 *
 * O banco guarda `open`, `doing`, `done`, `blocked`. Mostrar essas palavras
 * seria vazar o esquema para a tela; e traduzir no servidor prenderia a
 * tradução a uma língua. A tabela mora aqui, onde o texto já é português.
 */
const ESTADOS: Readonly<Record<string, { rotulo: string; classe: string }>> = {
  open: { rotulo: 'Em aberto', classe: 'aberta' },
  doing: { rotulo: 'Em andamento', classe: 'andamento' },
  done: { rotulo: 'Concluída', classe: 'concluida' },
  blocked: { rotulo: 'Bloqueada', classe: 'bloqueada' },
};

const PRAZO = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

/**
 * A tarefa que abre, e a que não abre.
 *
 * Só quem tem passo ganha triângulo. Um triângulo em toda tarefa seria a
 * interface prometendo conteúdo em todas elas — e a maioria abriria no vazio,
 * que é o jeito mais rápido de ensinar alguém a não clicar mais.
 *
 * O estado de aberto/fechado NÃO mora aqui: quem guarda é o App. Este cartão é
 * remontado a cada carga do painel (a `key` muda de propósito, para reencenar a
 * animação de "isto mudou"), e um `useState` local morreria junto — a lista
 * fecharia sozinha um segundo depois de a pessoa apertar "Executar", que é
 * exatamente o instante em que ela está olhando para lá.
 */
export function Tarefas({
  tarefas,
  passos,
  falhou,
  passosFalharam,
  abertas,
  onAlternar,
  disparados,
  dispensando,
  onExecutar,
  onDispensar,
}: {
  tarefas: readonly TarefaNaTela[];
  passos: readonly PassoNaTela[];
  falhou: boolean;
  passosFalharam: boolean;
  abertas: ReadonlySet<string>;
  onAlternar: (tarefaId: string) => void;
  disparados: ReadonlySet<string>;
  dispensando: ReadonlySet<string>;
  onExecutar: (passoId: string) => void;
  onDispensar: (passoId: string) => void;
}): JSX.Element {
  const hojeISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Os passos chegam achatados, numa consulta só para as vinte tarefas. O
  // agrupamento acontece uma vez por carga, e não uma vez por tarefa por render.
  const porTarefa = useMemo(() => {
    const mapa = new Map<string, PassoNaTela[]>();
    for (const p of passos) {
      const lista = mapa.get(p.tarefaId) ?? [];
      lista.push(p);
      mapa.set(p.tarefaId, lista);
    }
    // A ordem é o plano. O servidor já entrega ordenado; ordenar de novo custa
    // nada e impede que uma mudança lá vire um plano fora de ordem aqui — e um
    // plano fora de ordem põe o botão no passo errado.
    for (const lista of mapa.values()) lista.sort((a, b) => a.ordem - b.ordem);
    return mapa;
  }, [passos]);

  return (
    <section className="u1-card" aria-labelledby="u1-h-tarefas">
      <TopoDoCartao
        titulo="Tarefas planejadas"
        id="u1-h-tarefas"
        estado={
          falhou || passosFalharam ? 'parcial' : tarefas.length === 0 ? 'frio' : 'vivo'
        }
      />

      {falhou ? (
        <p className="u1-card__vazio u1-card__vazio--falha">Não consegui ler as tarefas agora.</p>
      ) : tarefas.length === 0 ? (
        <p className="u1-card__vazio">Nenhuma tarefa em aberto.</p>
      ) : (
        <ul className="u1-tarefas">
          {tarefas.map((t) => {
            const estado = ESTADOS[t.estado] ?? { rotulo: t.estado, classe: 'aberta' };
            // Atrasada é informação de estado, não de data: quem olha a lista
            // precisa ver o problema antes de ler a data e fazer a conta.
            const atrasada = t.prazo !== null && t.prazo < hojeISO && t.estado !== 'done';

            const meus = porTarefa.get(t.id) ?? [];
            const temPassos = meus.length > 0;
            const aberta = abertas.has(t.id);
            const listaId = `u1-passos-${t.id}`;
            const feitos = meus.filter((p) => p.estado === 'feito').length;

            return (
              <li key={t.id} className="u1-tarefa">
                <span className={`u1-selo u1-selo--${estado.classe}`}>{estado.rotulo}</span>

                {temPassos ? (
                  <button
                    type="button"
                    className="u1-tarefa__abrir"
                    aria-expanded={aberta}
                    aria-controls={listaId}
                    onClick={() => onAlternar(t.id)}
                  >
                    <span className="u1-tarefa__triangulo" aria-hidden="true">
                      ▸
                    </span>
                    <span className="u1-tarefa__titulo">{t.titulo}</span>
                    <span className="u1-oculto">
                      {' '}
                      — {meus.length} passo{meus.length === 1 ? '' : 's'}
                    </span>
                  </button>
                ) : (
                  <span className="u1-tarefa__titulo u1-tarefa__titulo--so">{t.titulo}</span>
                )}

                <span className="u1-tarefa__meta">
                  {t.equipe !== null && t.equipe !== '' && (
                    <span className="u1-tarefa__equipe">{t.equipe}</span>
                  )}
                  {temPassos && (
                    <span className="u1-tarefa__contagem">
                      {feitos}/{meus.length} feitos
                    </span>
                  )}
                  {t.prazo !== null && (
                    <span className={`u1-tarefa__prazo${atrasada ? ' u1-tarefa__prazo--atrasada' : ''}`}>
                      {atrasada ? 'venceu ' : ''}
                      {PRAZO.format(new Date(`${t.prazo}T12:00:00`))}
                    </span>
                  )}
                </span>

                {temPassos && aberta && (
                  <Passos
                    id={listaId}
                    passos={meus}
                    executavelId={proximoExecutavel(meus)}
                    disparados={disparados}
                    dispensando={dispensando}
                    onExecutar={onExecutar}
                    onDispensar={onDispensar}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Sem isto, uma consulta de passos que caiu faria toda tarefa parecer
          tarefa sem plano — e "não tem passo" e "não consegui ler os passos"
          são coisas diferentes. */}
      {!falhou && passosFalharam && (
        <p className="u1-card__vazio u1-card__vazio--falha">
          Não consegui ler os passos agora; as tarefas acima podem ter plano.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// conectores
// ---------------------------------------------------------------------------

/**
 * Os conectores, com um interruptor que **desliga de verdade**.
 *
 * Ligado quer dizer que o Will pode usar aquele conector nesta sessão: a
 * escolha vai ao servidor e sai do catálogo que o cérebro recebe. Ela só
 * restringe — o que o papel da pessoa não permite continua fora, ligado ou não,
 * e o servidor responde com o que de fato valeu.
 *
 * `planejado` não tem interruptor. Um botão que não faz nada é pior que a
 * ausência dele: promete controle que não existe.
 */
export function Conectores({
  conectores,
  ligados,
  onAlternar,
}: {
  conectores: readonly ConectorNaTela[];
  ligados: ReadonlySet<string>;
  onAlternar: (id: string, ligar: boolean) => void;
}): JSX.Element {
  return (
    <section className="u1-card" aria-labelledby="u1-h-conectores">
      {/* `parcial` enquanto houver integração planejada na lista, e isso é
          honesto: duas das nove existem. Um `LIVE` aqui afirmaria um conjunto
          completo que não temos. */}
      <TopoDoCartao
        titulo="Conectores"
        id="u1-h-conectores"
        estado={
          conectores.some((c) => c.estado === 'ligado')
            ? conectores.every((c) => c.estado === 'ligado')
              ? 'vivo'
              : 'parcial'
            : 'frio'
        }
      />
      <p className="u1-card__nota">O que o Will pode usar nesta conversa.</p>

      <ul className="u1-conectores">
        {conectores.map((c) => {
          const disponivel = c.estado === 'ligado';
          const ativo = disponivel && ligados.has(c.id);
          return (
            <li key={c.id} className={`u1-conector u1-conector--${c.estado}`}>
              <span className="u1-conector__nome">{c.nome}</span>

              <span className="u1-conector__modo">
                {c.estado === 'planejado'
                  ? 'ainda não'
                  : c.estado === 'sem_papel'
                    ? 'sem permissão'
                    : c.escrita
                      ? 'leitura e escrita'
                      : 'somente leitura'}
              </span>

              {disponivel ? (
                <button
                  type="button"
                  role="switch"
                  aria-checked={ativo}
                  className={`u1-chave${ativo ? ' u1-chave--on' : ''}`}
                  onClick={() => onAlternar(c.id, !ativo)}
                >
                  <span className="u1-chave__bolha" aria-hidden="true" />
                  <span className="u1-oculto">{ativo ? 'Desligar' : 'Ligar'} {c.nome}</span>
                </button>
              ) : (
                <span className="u1-conector__sem" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
